import { useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { Minus, Plus, Trash2, Tag, Gift, Sparkles, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { localizedPath } from "@/lib/url-slugs";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import keychainImg from "@/assets/keychain.png";
import { FREE_GIFT_CONFIG, qualifiesForFreeGift } from "@/config/free-gift";
import RecommendationSection from "@/components/recommendations/RecommendationSection";

const FREE_KEYCHAIN_THRESHOLD = FREE_GIFT_CONFIG.minSubtotal;

const CartDrawer = () => {
  const { items, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, subtotal, totalItems, appliedPromo, discountAmount, bundleDiscount } = useCart();
  const { t, lang } = useLanguage();
  const prevOpenRef = useRef(false);

  const total = Math.max(0, subtotal - discountAmount - (bundleDiscount || 0));
  const qualifiesForKeychain = qualifiesForFreeGift(subtotal);

  // Toast + confetti on cart open when qualifying
  useEffect(() => {
    if (isCartOpen && !prevOpenRef.current && qualifiesForKeychain) {
      const keychainText = lang === "lv"
        ? "Jūs saņemsiet bezmaksas atslēgu piekariņu!"
        : "You qualify for a free keychain!";

      toast(keychainText, {
        icon: <Gift size={16} className="text-primary" />,
        duration: 2600,
        position: "top-center",
        classNames: {
          toast: "min-h-0 max-w-[min(92vw,20rem)] rounded-md border border-primary/20 bg-background px-3 py-2 shadow-md",
          title: "text-xs font-medium leading-snug",
        },
      });

      confetti({
        particleCount: 60,
        spread: 70,
        origin: { x: 0.85, y: 0.3 },
        colors: ["#0F6B3A", "#D9A441", "#1E7F34", "#FFD700"],
        disableForReducedMotion: true,
        zIndex: 9999,
      });
    }
    prevOpenRef.current = isCartOpen;
  }, [isCartOpen, qualifiesForKeychain, lang]);

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display">{t("cart.your_cart")} ({totalItems})</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-muted-foreground">{t("cart.empty")}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Free gift — auto-added, qty 1, not removable */}
              {qualifiesForKeychain && (
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-primary/30 bg-primary/5">
                  <img src={keychainImg} alt={FREE_GIFT_CONFIG.name} className="h-12 w-12 object-contain flex-shrink-0 rounded bg-background" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground leading-tight">
                      {lang === "lv" ? FREE_GIFT_CONFIG.name : FREE_GIFT_CONFIG.nameEn}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                      {lang === "lv" ? "Daudzums: 1 · nav noņemams" : "Qty: 1 · cannot be removed"}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-primary flex-shrink-0">€0,00</span>
                </div>
              )}

              {items.map((item) => {
                const itemKey = item.variantId ? `${item.product.id}:${item.variantId}` : item.product.id;
                const price = item.variantPrice ?? item.product.price;
                return (
                  <div key={itemKey} className="flex gap-4 p-3 bg-muted rounded-lg">
                    <div className="w-16 h-16 rounded-md bg-muted flex-shrink-0 overflow-hidden">
                      {item.product.images?.[0] ? (
                        <img src={item.product.images[0]} alt={item.product.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs font-medium text-muted-foreground">{item.product.brand}</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.product.name}</p>
                      {item.variantLabel && <p className="text-xs text-muted-foreground">{item.variantLabel}</p>}
                      <p className="text-sm font-bold text-primary">€{price.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.variantId)} className="w-6 h-6 rounded flex items-center justify-center bg-background border hover:bg-muted transition-colors"><Minus size={12} /></button>
                        <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.variantId)} className="w-6 h-6 rounded flex items-center justify-center bg-background border hover:bg-muted transition-colors"><Plus size={12} /></button>
                        <button onClick={() => removeFromCart(item.product.id, item.variantId)} className="ml-auto p-1 text-destructive hover:text-destructive/80 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <RecommendationSection placement="cart_drawer" compact />
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("cart.subtotal")}</span>
                <span>€{subtotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && appliedPromo && (
                <div className="flex justify-between text-sm text-primary">
                  <span className="flex items-center gap-1"><Tag size={12} /> {appliedPromo.code}</span>
                  <span>-€{discountAmount.toFixed(2)}</span>
                </div>
              )}
              {bundleDiscount > 0 && (
                <div className="flex justify-between text-sm text-primary">
                  <span className="flex items-center gap-1"><Tag size={12} /> {lang === "lv" ? "Komplekta atlaide" : "Bundle"}</span>
                  <span>-€{bundleDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-display font-semibold text-lg">
                <span>{t("cart.total")}</span><span>€{total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t("cart.vat_included")}</p>
              <p className="text-xs text-muted-foreground">{t("cart.shipping_at_checkout")}</p>
              <Button asChild className="w-full" size="lg">
                <Link to={localizedPath(lang, "cart")} onClick={() => setIsCartOpen(false)}>{t("cart.view_checkout")}</Link>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
